<template>
  <div class="min-h-screen bg-gray-50">
    <div id="dropdown" />

    <!-- Topbar -->
    <header class="sticky top-0 z-30 border-b border-brand-800/40 bg-brand-600 shadow-sm">
      <div class="flex items-center justify-between gap-4 px-4 py-3 md:px-5">
        <div class="flex items-center gap-3 min-w-0">
          <Link href="/" class="flex items-center gap-2.5 shrink-0">
            <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
              <img alt="KTA CMS" src="/images/logo-only.png" class="h-5 w-5" />
            </span>
            <div class="hidden sm:block leading-tight">
              <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">
                OK Bank
              </p>
              <p class="text-sm font-bold text-white">
                KTA CMS
              </p>
            </div>
          </Link>

          <div class="hidden md:block h-6 w-px bg-white/20" />

          <div class="hidden md:block min-w-0">
            <p class="truncate text-sm font-semibold text-white">
              {{ pageTitle }}
            </p>
            <p class="truncate text-[11px] text-white/60">
              {{ pageSubtitle }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <dropdown class="md:hidden" placement="bottom-end">
            <template #default>
              <button
                type="button"
                class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/15"
                aria-label="Open menu"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
                </svg>
              </button>
            </template>
            <template #dropdown>
              <div class="mt-2 w-64 rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow-xl">
                <main-menu />
              </div>
            </template>
          </dropdown>

          <div class="hidden sm:flex items-center rounded-xl bg-white/10 px-3 py-1.5 ring-1 ring-white/15">
            <span
              class="mr-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
              :class="isAgency ? 'bg-amber-400/20 text-amber-100' : 'bg-teal-400/20 text-teal-100'"
            >
              {{ isAgency ? 'Agency' : 'Admin' }}
            </span>
            <span class="text-xs font-medium text-white/80">
              {{ auth.user.first_name }} {{ auth.user.last_name }}
            </span>
          </div>

          <dropdown placement="bottom-end">
            <template #default>
              <button
                type="button"
                class="group inline-flex items-center gap-2 rounded-xl bg-white px-2.5 py-1.5 text-sm font-semibold text-brand-800 shadow-sm transition hover:bg-brand-50"
              >
                <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600 text-[11px] font-bold text-white">
                  {{ userInitials }}
                </span>
                <span class="hidden md:inline">Account</span>
                <icon class="h-4 w-4 fill-brand-500" name="cheveron-down" />
              </button>
            </template>
            <template #dropdown>
              <div class="mt-2 w-56 overflow-hidden rounded-2xl border border-gray-200 bg-white py-2 text-sm shadow-xl">
                <div class="border-b border-gray-100 px-4 py-3">
                  <p class="font-semibold text-brand-900">
                    {{ auth.user.first_name }} {{ auth.user.last_name }}
                  </p>
                  <p class="mt-0.5 text-xs text-gray-500">
                    {{ auth.user.email || (isAgency ? 'Agency account' : 'Admin account') }}
                  </p>
                </div>
                <Link
                  class="block px-4 py-2.5 text-gray-700 hover:bg-brand-50 hover:text-brand-800"
                  href="/password"
                >
                  Change Password
                </Link>
                <Link
                  v-if="!isAgency"
                  class="block px-4 py-2.5 text-gray-700 hover:bg-brand-50 hover:text-brand-800"
                  :href="`/users/${auth.user.id}/edit`"
                >
                  My Profile
                </Link>
                <Link
                  v-if="!isAgency"
                  class="block px-4 py-2.5 text-gray-700 hover:bg-brand-50 hover:text-brand-800"
                  href="/users"
                >
                  Manage Users
                </Link>
                <Link
                  class="block w-full px-4 py-2.5 text-left text-red-600 hover:bg-red-50"
                  href="/logout"
                  method="delete"
                  as="button"
                >
                  Logout
                </Link>
              </div>
            </template>
          </dropdown>
        </div>
      </div>
    </header>

    <!-- Body -->
    <div class="md:flex md:min-h-[calc(100vh-61px)]">
      <aside class="hidden w-60 shrink-0 border-r border-gray-200 bg-white md:block">
        <main-menu class="px-3 py-5" />
      </aside>

      <main class="min-w-0 flex-1 px-4 py-5 md:px-5 md:py-6" scroll-region>
        <div class="w-full max-w-none">
          <flash-messages />
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Dropdown from '@/Shared/Dropdown'
import MainMenu from '@/Shared/MainMenu'
import FlashMessages from '@/Shared/FlashMessages'

export default {
  components: {
    Dropdown,
    FlashMessages,
    Icon,
    Link,
    MainMenu,
  },
  props: {
    auth: Object,
  },
  computed: {
    isAgency() {
      return this.auth.user?.role === 'agency'
    },
    userInitials() {
      const first = this.auth.user?.first_name?.[0] || ''
      const last = this.auth.user?.last_name?.[0] || ''
      return `${first}${last}`.toUpperCase() || 'U'
    },
    currentPath() {
      return (this.$page?.url || '/').replace(/^\//, '').split('?')[0]
    },
    pageTitle() {
      const path = this.currentPath
      if (!path) return 'Dashboard'
      if (path.startsWith('custmanagement')) return 'Tracking Management'
      if (path.startsWith('campaign')) return 'Campaign'
      if (path.startsWith('channel')) return 'Channel'
      if (path.startsWith('pending-submissions')) return 'Pending Submissions'
      if (path.startsWith('submissionweb')) return 'Submission (Web)'
      if (path.startsWith('submissionmobile')) return 'Submission (App)'
      if (path.startsWith('users')) return 'Users'
      return 'OK KTA CMS'
    },
    pageSubtitle() {
      const path = this.currentPath
      if (!path) return 'Submission overview and insights'
      if (path.startsWith('custmanagement')) return 'Track user journey and conversion steps'
      if (path.startsWith('campaign')) return 'Manage campaign links and performance'
      if (path.startsWith('channel')) return 'Configure acquisition channels'
      if (path.startsWith('pending-submissions')) return 'Admin retry for parked / slow Pefindo cases'
      if (path.startsWith('submissionweb')) return 'Web landing submissions'
      if (path.startsWith('submissionmobile')) return 'Mobile app submissions'
      if (path.startsWith('users')) return 'User access and roles'
      return 'Internal operations console'
    },
  },
}
</script>
